import React, { useState } from 'react';
import { Download, BookOpen, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { api, PDF_URL } from '../lib/api';

const SampleChapterCTA = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | success | error
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setErr('Please enter a valid email.');
      return;
    }
    setState('loading');
    setErr('');
    try {
      await api.chapterRequest({ email: email.trim(), name: name.trim() || undefined });
      setState('success');
      // Trigger the download after a short beat so the success UI is visible
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = PDF_URL;
        a.download = 'Gasoline-I-Chapter-1.pdf';
        a.click();
      }, 400);
    } catch (e2) {
      setErr(e2.message || 'Something went wrong.');
      setState('error');
    }
  };

  return (
    <section id="sample-chapter" className="relative py-20 bg-gradient-to-b from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-[#D4AF37]/30 rounded-3xl p-8 md:p-12">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1 mb-4">
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-bold text-xs tracking-wider">FREE PREVIEW</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              Read the first chapter of{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">Gasoline I</span>
            </h2>
            <p className="text-gray-300 leading-relaxed">
              Get a real taste of the trauma-informed curriculum. One chapter. One reflection prompt. Yours to keep.
            </p>
          </div>

          <div className="lg:col-span-3">
            {state === 'success' ? (
              <div
                className="bg-[#0a0a0a] border border-[#D4AF37]/40 rounded-2xl p-8 text-center"
                data-testid="chapter-success"
              >
                <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                  Your chapter is downloading
                </h3>
                <p className="text-gray-400 mb-6">
                  If the download did not start automatically, click below.
                </p>
                <a
                  href={PDF_URL}
                  download="Gasoline-I-Chapter-1.pdf"
                  data-testid="chapter-manual-download"
                  className="inline-flex items-center gap-2 text-[#D4AF37] font-bold hover:underline"
                >
                  <Download className="w-5 h-5" />
                  Download the sample chapter
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="chapter-form">
                <div>
                  <label className="block text-xs font-bold text-gray-400 mb-2 tracking-wider">YOUR NAME (OPTIONAL)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="chapter-name"
                    placeholder="What should we call you?"
                    className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-lg px-4 py-3 text-white placeholder:text-gray-600 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 mb-2 tracking-wider">EMAIL ADDRESS</label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-500 absolute left-3 top-3.5" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="chapter-email"
                      required
                      placeholder="you@example.com"
                      className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-gray-600 transition-all"
                    />
                  </div>
                </div>
                {err && <p className="text-[#E63946] text-sm" data-testid="chapter-error">{err}</p>}
                <Button
                  type="submit"
                  disabled={state === 'loading'}
                  data-testid="chapter-submit"
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold py-6 text-lg transition-all duration-300 transform hover:scale-[1.02]"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {state === 'loading' ? 'SENDING...' : 'SEND ME THE CHAPTER'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  We will never sell your email. Occasional letters about ceremonies, new releases, and the mission.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SampleChapterCTA;
